import { css, html, LitElement, type PropertyValueMap } from "lit";
import { customElement, property } from "lit/decorators.js";
import { createRef, ref } from "lit/directives/ref.js";
import { ResizeController } from "@lit-labs/observers/resize-controller.js";
import classNames from "classnames";

import { HighlightController } from "../../utils/highlight";
import { ScrollController } from "../../utils/scroll";
import { LookupController } from "../../utils/lookup";
import { styles } from "../../utils/zoomable-scrollable-slide.styles";

import type { Ref } from "lit/directives/ref.js";
import type { SlideBackgroundColor, SlideState } from "../../types.d";
import type { SlideViewMode } from "../slide/slide";

const CONTENT_VERTICAL_PADDING = 40;

const DEFAULT_SLIDE_HEIGHT = 540;
const DESKTOP_MAX_TITLE_HEIGHT = 106;
const DESKTOP_MIN_TITLE_HEIGHT = 88;
const MOBILE_MAX_TITLE_HEIGHT = 116;
const MOBILE_MIN_TITLE_HEIGHT = 98;

@customElement("bp-scrollable-slide")
export class ScrollableSlide extends LitElement {
  static styles = [
    styles,
    css`
      ::slotted([slot="content"]) {
        color: var(--color-black);
        font-family: var(--font-sans);
        font-size: var(--font-size-4xl);
        font-weight: var(--font-weight-normal);
        line-height: var(--line-height-snug);
        width: var(--slide-default-width);
        box-sizing: border-box;
        margin: var(--size-5) 0;
        padding: 0 var(--size-5);
        text-align: start;
        unicode-bidi: plaintext;
      }
    `,
  ];

  slideHeight = DEFAULT_SLIDE_HEIGHT;

  get minTitleHeight() {
    return this.viewMode === "default"
      ? DESKTOP_MIN_TITLE_HEIGHT
      : MOBILE_MIN_TITLE_HEIGHT;
  }

  get maxTitleHeight() {
    return this.viewMode === "default"
      ? DESKTOP_MAX_TITLE_HEIGHT
      : MOBILE_MAX_TITLE_HEIGHT;
  }

  get maxContentWindowHeight() {
    return this.slideHeight - this.minTitleHeight;
  }

  get minContentWindowHeight() {
    return this.slideHeight - this.maxTitleHeight;
  }

  slideRef: Ref<HTMLElement> = createRef();
  contentRef: Ref<HTMLElement> = createRef();
  windowRef: Ref<HTMLElement> = createRef();

  private slideObserver = new ResizeController(this, {
    callback: (entries) => {
      entries
        .filter((entry) => entry.target.className.includes("scrollable-slide"))
        .forEach((entry) => {
          this.slideHeight = entry.contentRect.height;
          this.scrollController.minWindowHeight = this.minContentWindowHeight;
          this.scrollController.maxWindowHeight = this.maxContentWindowHeight;
          this.updateState();
        });
    },
  });

  @property({ attribute: "background-color" })
  backgroundColor?: SlideBackgroundColor = "white";

  @property({
    type: Boolean,
    attribute: "hide-shadow",
  })
  hideShadow = false;

  @property()
  slideState?: SlideState;

  @property({ attribute: "view-mode" })
  viewMode?: SlideViewMode = "default";

  highlightController = new HighlightController();
  lookupController = new LookupController({
    windowRef: this.windowRef,
  });
  scrollController = new ScrollController({
    slideRef: this.slideRef,
    verticalPadding: CONTENT_VERTICAL_PADDING,
    minWindowHeight: this.minContentWindowHeight,
    maxWindowHeight: this.maxContentWindowHeight,
  });

  handleContentUpdate(event: any) {
    const contentEl = event.target?.assignedNodes({
      flatten: true,
    })[0];
    this.highlightController.contentEl = contentEl;
    this.lookupController.contentEl = contentEl;
    this.scrollController.contentEl = contentEl;
    this.updateState();
  }

  updateState() {
    this.highlightController.update(this.slideState?.highlightState || {});
    this.lookupController.update(this.slideState?.lookupState || {});
    this.scrollController.update(this.slideState?.transformState || {});
  }

  protected willUpdate(
    changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>,
  ): void {
    if (changedProperties.has("slideState")) {
      this.updateState();
    }
  }

  render() {
    // This will be overridden by scroll controller.
    const initStyle = "--scroll-transition: 0; --title-transition: 0;";
    const overflowShadowStyles = classNames("overflow-shadow", {
      "overflow-shadow-hidden": this.hideShadow,
    });

    return html`
      <div
        ${ref(this.slideRef)}
        class="scrollable-slide ${this.viewMode} background-${this
          .backgroundColor}"
        style="${initStyle}"
      >
        <slot name="title"></slot>
        <div class="content-window" ${ref(this.windowRef)}>
          <div class="transformed-content" ${ref(this.contentRef)}>
            <div class="content">
              <slot
                name="content"
                @slotchange=${this.handleContentUpdate}
              ></slot>
            </div>
          </div>
        </div>
        <div class="${overflowShadowStyles}"></div>
      </div>
    `;
  }

  firstUpdated() {
    if (this.slideRef?.value) {
      this.slideObserver.observe(this.slideRef.value);
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "bp-scrollable-slide": ScrollableSlide;
  }
}
